import { useCallback, useMemo, useState } from 'react'

import { modelStackLabel } from '../../../lib/optimization/modelStacks'
import type { OptimizationQualityEval } from '../../../lib/optimization/qualityEval'
import {
  collectQualityIssues,
  groupQualityIssuesBySection,
  type QualityIssue,
} from '../../../lib/optimization/qualityEvalFlagging'
import type { SkillGapSummary } from '../../../lib/optimization/skillGapRecommendations'
import { supabase } from '../../../lib/supabase'
import OptimizationSkillsGapPanel from './OptimizationSkillsGapPanel'

export interface OptimizationFeedbackContentProps {
  runId: string
  qualityEval: OptimizationQualityEval
  skillGapSummary?: SkillGapSummary | null
  qualityIssues?: QualityIssue[]
  liveSummaryProgrammaticPass?: boolean
  /** Compact layout inside the tailoring agent side panel. */
  variant?: 'modal' | 'inline'
  onRatingSaved?: () => void
}

export default function OptimizationFeedbackContent({
  runId,
  qualityEval,
  skillGapSummary,
  qualityIssues,
  liveSummaryProgrammaticPass,
  variant = 'modal',
  onRatingSaved,
}: OptimizationFeedbackContentProps) {
  const [rating, setRating] = useState<number | null>(null)
  const [notes, setNotes] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  const handleSaveRating = useCallback(async () => {
    if (!rating) {
      setSaveError('Pick a 1–5 quality score before saving.')
      return
    }
    setSaving(true)
    setSaveError(null)
    try {
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser()
      if (userError || !user) throw new Error('Sign in required.')

      const { error } = await supabase.from('optimization_lab_ratings').upsert(
        {
          run_id: runId,
          user_id: user.id,
          model_stack: qualityEval.model_stack,
          rating,
          notes: notes.trim() || null,
        },
        { onConflict: 'run_id,user_id' }
      )
      if (error) throw new Error(error.message)
      setSaved(true)
      onRatingSaved?.()
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e))
    } finally {
      setSaving(false)
    }
  }, [notes, onRatingSaved, qualityEval.model_stack, rating, runId])

  const { primary_pass } = qualityEval
  const gates = qualityEval.gates ?? {
    all_blocks_done: false,
    zero_failed: false,
    summary_block_done: false,
    work_experience_blocks_done: false,
  }
  const summaryProgrammaticPass =
    liveSummaryProgrammaticPass === false
      ? false
      : liveSummaryProgrammaticPass === true
        ? true
        : gates.summary_programmatic_pass
  const issues = useMemo(
    () => qualityIssues ?? collectQualityIssues(qualityEval),
    [qualityIssues, qualityEval]
  )
  const issueGroups = useMemo(() => groupQualityIssuesBySection(issues), [issues])
  const needsReview = !primary_pass && issues.length > 0
  const statusLabel = needsReview ? 'Needs review' : primary_pass ? 'Ready' : 'Needs review'
  const statusTone = primary_pass
    ? 'bg-brand-primary-subtle text-brand-primary-strong border-brand-primary/20'
    : 'bg-amber-50 text-amber-900 border-amber-200'
  const inline = variant === 'inline'

  return (
    <div
      className={inline ? 'space-y-3' : 'min-h-0 flex-1 overflow-y-auto px-4 py-3 space-y-3'}
      data-testid={inline ? 'optimization-feedback-inline' : undefined}
    >
      {!inline && (
        <p className="text-xs text-text-secondary">
          Automated checks ({modelStackLabel(qualityEval.model_stack)}). Page stays interactive.
        </p>
      )}
      <div
        className={`rounded-lg px-3 py-2 text-sm font-medium border ${statusTone}`}
        data-testid="quality-primary-pass"
      >
        Overall result: {statusLabel}
      </div>
      <ul className="text-sm text-text-secondary space-y-1">
        <li>
          {gates.all_blocks_done ? '✓' : '✗'} All sections finished (
          {qualityEval.blocks_done}/{qualityEval.blocks_total})
        </li>
        <li>{gates.zero_failed ? '✓' : '✗'} No failed sections</li>
        <li>{gates.summary_block_done ? '✓' : '✗'} Professional summary done</li>
        <li>{gates.work_experience_blocks_done ? '✓' : '✗'} Work experience sections done</li>
        {gates.contact_has_name != null && (
          <li>{gates.contact_has_name ? '✓' : '✗'} Contact name on resume</li>
        )}
        {gates.target_title_set != null && (
          <li>{gates.target_title_set ? '✓' : '✗'} Target title set</li>
        )}
        {gates.summary_has_text != null && (
          <li>{gates.summary_has_text ? '✓' : '✗'} Summary has real text (not NA)</li>
        )}
        {gates.summary_programmatic_pass != null && (
          <li>{summaryProgrammaticPass ? '✓' : '✗'} Summary passes format checks</li>
        )}
        {gates.education_programmatic_pass != null && (
          <li>{gates.education_programmatic_pass ? '✓' : '✗'} Education passes format checks</li>
        )}
      </ul>
      {needsReview && issueGroups.length > 0 && (
        <div
          className="rounded-lg border border-amber-200 bg-amber-50/80 px-3 py-2"
          data-testid="quality-issues-list"
        >
          <p className="text-sm font-medium text-amber-950 mb-2">What to fix</p>
          <ul className="text-sm text-amber-900 space-y-3">
            {issueGroups.map((group) => (
              <li key={group.sectionLabel}>
                <span className="font-medium text-amber-950">{group.sectionLabel}</span>
                <ul className="mt-1 ml-1 list-disc list-outside space-y-1 pl-4 text-amber-900/95">
                  {group.recommendations.map((rec, recIndex) => (
                    <li key={`${recIndex}-${rec}`} className="text-xs sm:text-sm">
                      {rec}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
          <p className="text-xs text-amber-800 mt-2">
            Highlighted sections on the left need your edits. We do not invent experience or skills
            without your data.
          </p>
        </div>
      )}
      {!primary_pass && issueGroups.length === 0 && (
        <p className="text-sm text-brand-primary" data-testid="quality-issues-cleared">
          You addressed the flagged items. Re-run tailoring if you want a fresh quality check.
        </p>
      )}
      {skillGapSummary && (
        <OptimizationSkillsGapPanel summary={skillGapSummary} variant="modal" />
      )}
      <div className={`border-t border-border-default pt-3 ${inline ? '' : ''}`}>
        <p className="text-sm font-medium text-text-primary mb-2">Your quality score (for A/B lab)</p>
        <div className="flex gap-2" data-testid="quality-rating-stars">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => setRating(n)}
              className={`h-9 w-9 rounded-full text-sm font-semibold border ${
                rating === n
                  ? 'bg-brand-primary text-white border-interactive'
                  : 'bg-surface-default text-text-secondary border-border-default hover:border-interactive'
              }`}
            >
              {n}
            </button>
          ))}
        </div>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          placeholder="Optional notes (JSON issues, summary tone, …)"
          className="mt-2 w-full rounded-lg border border-border-default px-3 py-2 text-sm"
        />
        {saveError && <p className="mt-2 text-sm text-red-600">{saveError}</p>}
        {saved && (
          <p className="mt-2 text-sm text-brand-primary" data-testid="quality-rating-saved">
            Rating saved for lab comparison.
          </p>
        )}
        <div className={`flex justify-end gap-2 ${inline ? 'mt-3' : 'mt-3'}`}>
          <button
            type="button"
            data-testid="save-quality-rating"
            onClick={() => void handleSaveRating()}
            disabled={saving}
            className="rounded-lg bg-brand-primary px-4 py-2 text-sm font-medium text-white hover:bg-brand-primary-hover disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save lab rating'}
          </button>
        </div>
      </div>
    </div>
  )
}
